import PropTypes from 'prop-types';

import { ButtonV2, CloseLargeIcon, ModalV2 as Modal, Tabs } from '@utilities';

import Visualization from '../../../../../../components/NarrativeFlowView/components/Visualization';
import { applyDraftChartColors } from '../../../../../../components/NarrativeFlowView/utilities/helpers';
import {
  buildDraftTopicData,
  getTopicLibraryCardIcon,
  getVariantSignature,
} from '../../../../utilities/helpers';

import styles from './_index.module.scss';

const ConfigureModal = ({
  activeConfigCard,
  activeConfigPreviewDraft = null,
  activeConfigSummary = [],
  isSaveDisabled = false,
  occupiedTopicIdSet,
  onCancel,
  onSave,
  onVariantChange,
}) => {
  if (!activeConfigCard) return null;

  const activeConfigVariantSignature = getVariantSignature(
    activeConfigCard?.selectedVariant,
    activeConfigCard?.config?.signatureDimensions
  );
  const activeConfigTopic =
    activeConfigCard?.config?.variantMap?.[activeConfigVariantSignature] || null;
  // Apply primary/secondary fallback only for two-dimension BYO groups.
  // One-dimension groups continue to use exact-match availability rules.
  const variantDimensions = Array.isArray(activeConfigCard?.config?.variantDimensions)
    ? activeConfigCard.config.variantDimensions
    : [];
  const primaryDimension = variantDimensions[0];
  const hasTwoDimensions = variantDimensions.length === 2;
  const fallbackActiveConfigPreviewDraft = activeConfigTopic
    ? {
        cleanTitle: activeConfigTopic?.name || activeConfigCard?.title,
        componentId: activeConfigTopic?.id,
        dataset: {
          isDraftDisplayData: true,
          type: activeConfigTopic?.vizType || activeConfigCard?.vizType || null,
        },
        title: activeConfigTopic?.name || activeConfigCard?.title,
        visualizationType: activeConfigTopic?.vizType || activeConfigCard?.vizType || null,
      }
    : null;
  const activeConfigPreviewTopicData = buildDraftTopicData(
    activeConfigPreviewDraft ||
      fallbackActiveConfigPreviewDraft || { title: activeConfigCard?.title }
  );
  const activeConfigPreviewDataset =
    activeConfigPreviewTopicData?.visualization?.datasets?.[0] || null;
  const activeConfigDraftPreviewDataset = activeConfigPreviewDataset
    ? applyDraftChartColors(activeConfigPreviewDataset)
    : null;
  const primaryActionLabel =
    activeConfigCard?.previousTopicId != null ? 'Update Topic Card' : 'Insert Topic Card';

  return (
    <Modal
      className={styles['configure-modal']}
      hasCloseIcon={false}
      isActive
      onClose={() => onCancel?.(activeConfigCard)}
      title={null}
    >
      <div className={styles['configure-modal-top']} data-testid="narratives-canvas-config-modal">
        <div className={styles['configure-modal-header-row']}>
          <div>{getTopicLibraryCardIcon(activeConfigCard?.vizType)}</div>
          <div className={styles['configure-modal-header-content']}>
            <div className={styles['configure-modal-title-row']}>
              <h2
                className={styles['configure-modal-title-text']}
                data-testid="narratives-canvas-config-modal-title"
              >
                {activeConfigCard?.config?.grouping}
              </h2>
            </div>
            <div className={styles['configure-modal-controls']}>
              {activeConfigCard?.config?.variantDimensions?.map((dimension) => (
                <div
                  className={styles['configure-modal-control-row']}
                  data-testid={`narratives-canvas-config-modal-control-${dimension}`}
                  key={dimension}
                >
                  <strong className={styles['configure-modal-control-label']}>{dimension}</strong>
                  <Tabs
                    className={styles['configure-modal-tabs']}
                    onChange={(value) => onVariantChange?.(activeConfigCard.id, dimension, value)}
                    options={(activeConfigCard?.config?.variantOptions?.[dimension] || []).map(
                      (option) => {
                        let isDisabled = true;

                        if (hasTwoDimensions && dimension === primaryDimension) {
                          const variantMapEntries = Object.values(
                            activeConfigCard?.config?.variantMap || {}
                          );
                          isDisabled = !variantMapEntries.some(
                            (topic) =>
                              topic?.variant?.[dimension] === option &&
                              topic?.id != null &&
                              (!occupiedTopicIdSet?.has(String(topic.id)) ||
                                String(topic.id) === String(activeConfigCard?.previousTopicId))
                          );
                        } else {
                          const nextSelectedVariant = {
                            ...(activeConfigCard?.selectedVariant || {}),
                            [dimension]: option,
                          };
                          const nextSignature = getVariantSignature(
                            nextSelectedVariant,
                            activeConfigCard?.config?.signatureDimensions
                          );
                          const nextTopic = activeConfigCard?.config?.variantMap?.[nextSignature];
                          isDisabled =
                            !nextTopic?.id ||
                            (occupiedTopicIdSet?.has(String(nextTopic.id)) &&
                              String(nextTopic.id) !== String(activeConfigCard?.previousTopicId));
                        }

                        return {
                          isDisabled,
                          label: option,
                          value: option,
                        };
                      }
                    )}
                    size="small"
                    value={activeConfigCard?.selectedVariant?.[dimension]}
                    variant="rounded"
                  />
                </div>
              ))}
            </div>
          </div>
          <button
            aria-label="Close"
            className={styles['configure-modal-close']}
            onClick={() => onCancel?.(activeConfigCard)}
            type="button"
          >
            <CloseLargeIcon />
          </button>
        </div>
      </div>
      <div className={styles['configure-modal-content']}>
        <p
          className={styles['configure-modal-summary']}
          data-testid="narratives-canvas-config-modal-summary"
        >
          {activeConfigSummary.map((part, index) => (
            <span key={`${part}-${index}`}>
              {index > 0 && (
                <span className={styles['configure-modal-summary-separator']}> | </span>
              )}
              {part}
            </span>
          ))}
        </p>
        {activeConfigPreviewDataset && (
          <>
            <div
              className={styles['configure-modal-preview']}
              data-testid="narratives-canvas-config-modal-preview"
            >
              <Visualization
                dataset={activeConfigDraftPreviewDataset}
                isDraftCard
                useWindowScroll
              />
            </div>
          </>
        )}
      </div>
      <div className={styles['configure-modal-footer']}>
        <ButtonV2
          data-testid="narratives-canvas-config-modal-cancel"
          onClick={() => onCancel?.(activeConfigCard)}
          shape="round"
        >
          Cancel
        </ButtonV2>
        <ButtonV2
          data-testid="narratives-canvas-config-modal-save"
          disabled={isSaveDisabled}
          onClick={() => onSave?.(activeConfigCard)}
          shape="round"
          variant="primary"
        >
          {primaryActionLabel}
        </ButtonV2>
      </div>
    </Modal>
  );
};

ConfigureModal.propTypes = {
  activeConfigCard: PropTypes.shape({
    config: PropTypes.shape({
      grouping: PropTypes.string,
      signatureDimensions: PropTypes.arrayOf(PropTypes.string),
      variantDimensions: PropTypes.arrayOf(PropTypes.string),
      variantMap: PropTypes.object,
      variantOptions: PropTypes.object,
    }),
    id: PropTypes.string,
    occupiedTopicIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    previousTopicId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    selectedVariant: PropTypes.object,
    title: PropTypes.string,
    vizType: PropTypes.string,
  }),
  activeConfigPreviewDraft: PropTypes.shape({
    cleanTitle: PropTypes.string,
    componentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dataset: PropTypes.object,
    sampleSize: PropTypes.number,
    title: PropTypes.string,
    visualizationType: PropTypes.string,
  }),
  activeConfigSummary: PropTypes.arrayOf(PropTypes.string),
  isSaveDisabled: PropTypes.bool,
  occupiedTopicIdSet: PropTypes.instanceOf(Set),
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  onVariantChange: PropTypes.func,
};

export default ConfigureModal;
